import api from './axios';

/**
 * Create a new submission with files
 * @param {Object} formData - The form data from the submission form
 * @returns {Promise} - Promise with the submission response
 */
export const createSubmission = async (formData) => {
  try {
    // Create a FormData object to handle file uploads
    const data = new FormData();

    // Add all form fields to the FormData
    Object.keys(formData).forEach(key => {
      // Skip the files array, we'll add it separately
      if (key !== 'files') {
        const value = formData[key];
        if (value !== null && typeof value === 'object' && !(value instanceof File) && !(value instanceof Blob)) {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value);
        }
      }
    });

    // Add each file to the FormData
    if (formData.files && formData.files.length > 0) {
      formData.files.forEach(file => {
        data.append('files', file);
      });
    }

    // Set the Content-Type to multipart/form-data for file uploads
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 30000
    };

    const response = await api.post('/submissions', data, config);
    return response.data.data; // Return the data directly for consistency
  } catch (error) {
    console.error('Error creating submission:', error);
    throw error;
  }
};

/**
 * Get all submissions for the logged-in user
 * @returns {Promise} - Promise with the submissions data
 */
export const getUserSubmissions = async () => {
  try {
    const response = await api.get('/submissions');
    return response.data;
  } catch (error) {
    console.error('Error fetching submissions:', error);
    throw error;
  }
};

/**
 * Get a specific submission by ID
 * @param {number} id - Submission ID to fetch
 * @returns {Promise} - Promise with the submission data
 */
export const getSubmissionById = async (id) => {
  try {
    const response = await api.get(`/submissions/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching submission details:', error);
    throw error;
  }
};

/**
 * Update submission status
 * @param {number} id - Submission ID to update
 * @param {string} status - New status ('Pending', 'Completed', or 'Cancelled')
 * @returns {Promise} - Promise with the updated submission
 */
export const updateSubmissionStatus = async (id, status) => {
  try {
    const response = await api.put(`/submissions/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating submission status:', error);
    throw error;
  }
};


export const cancelSubmission = async (id, userTeamData) => {
  try {
    const { data } = await api.put(`/submissions/${id}/status`, {
      status: 'Cancelled',
      user_role_id: userTeamData?.user_role_id,
      user_id: userTeamData?.id,
      user_email: userTeamData?.email
    });

    if (!data?.status) {
      throw new Error(data?.message || 'Unexpected response from server');
    }

    return 'Submission cancelled successfully' || data.message;
  } catch (error) {
    if (error.response?.status === 401) {
      window.location.href = '/login';
      return;
    }

    throw new Error(error.response?.data?.message || error.message || 'Failed to cancel submission');
  }
};

export const getDashboardStats = async (range, offset, userTeamData) => {
  try {
    const data = new FormData();
    data.append('user_team_id', userTeamData?.id);
    data.append('user_role_id', userTeamData?.user_role_id);
    userTeamData?.user_group?.forEach(group => data.append('user_group[]', group));

    const response = await api.post(
      `/submissions/dashboard/stats?range=${range}&offset=${offset}`,
      data
    );

    if (!response.data?.status) {
      throw new Error(response.data?.message || 'Invalid response from server');
    }

    return response.data.data;
  } catch (error) {
    if (error.response?.status === 401) {
      window.location.href = '/login';
      return;
    }

    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch dashboard stats');
  }
};

export const exportEmailStatistics = async (userTeamData) => {
  try {
    const response = await api.post(
      '/exportMonthlyEmailStatistics',
      { user_role_id: userTeamData?.user_role_id },
      { responseType: 'blob' }
    );

    return {data: response.data, message: 'Email statistics exported successfully'};
  } catch (error) {
    if (error.response?.status === 401) {
      window.location.href = '/login';
      return;
    }

    if (error.response?.status === 403) {
      throw new Error('Access denied. Admin role required.');
    }

    throw new Error(error.response?.data?.message || error.message || 'Failed to export email statistics');
  }
};

export const getDocxSubjectTextFromUrl = async (url) => {
  try {
    const response = await api.get(`/submissions/extract-subject-docx-text?url=${encodeURIComponent(url)}`);
    return response.data;
  } catch (error) {
    console.error('Error extracting DOCX subject text:', error);
    throw error;
  }
};

export const reassignSubmission = async (id, data) => {
  try {
    const response = await api.put(`/submissions/${id}/reassign`, data);
    return response.data;
  } catch (error) {
    console.error('Error reassigning submission:', error);
    throw error;
  }
};

export const getDocxReplaceFieldsFromUrl = async (urls, templateTypes = null, recipientTypes = null) => {
  try {
    let endpoint = `/submissions/extract-docx-replace-fields?urls=${encodeURIComponent(urls)}`;
    if (templateTypes) {
      endpoint += `&templateTypes=${encodeURIComponent(templateTypes)}`;
    }
    if (recipientTypes) {
      endpoint += `&recipientTypes=${encodeURIComponent(recipientTypes)}`;
    }

    const response = await api.get(endpoint, {
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    console.error('Error extracting DOCX text:', error);
    throw error;
  }
};
